import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  api,
  REST_AUTH_EXAMPLE,
  type ApiKey,
  type CreatedApiKey,
} from "../lib/api";
import { CopyButton } from "../components/CopyButton";
import { SCOPES, SCOPE_LABEL } from "../lib/scopes";
import { relativeTime } from "../lib/time";
import keySvg from "../theme/icons/apikeys-key.svg?raw";

export function ApiKeys() {
  // null = not yet fetched (same sentinel convention as ConnectedApps /
  // Watchlists) so the empty state never flashes before the first load.
  const [keys, setKeys] = useState<ApiKey[] | null>(null);
  const [name, setName] = useState("");
  const [scopes, setScopes] = useState<string[]>(["screen:read"]);
  // The scopes shown on the reveal come from what we SUBMITTED, not the
  // POST response — POST /keys returns only the secret + identifiers, no
  // `scopes` (see CreatedApiKey in lib/api).
  const [created, setCreated] = useState<
    (CreatedApiKey & { scopes: string[] }) | null
  >(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  // Per-key two-step revoke: revoking silently breaks live integrations,
  // so the first click arms a confirm rather than firing immediately.
  const [confirming, setConfirming] = useState<string | null>(null);
  // Revoked keys are kept for the audit/usage trail (usage_event.key_id and
  // watchlist.api_key_id FK them), so they accumulate; hide them by default
  // to keep the list focused on keys you can still use.
  const [showRevoked, setShowRevoked] = useState(false);

  const refresh = () =>
    api
      .listKeys()
      .then((r) => setKeys(r.keys))
      .catch((e) => setErr(String(e.message)));

  useEffect(() => {
    refresh();
  }, []);

  const canCreate = !busy && name.trim().length > 0 && scopes.length > 0;

  async function create(e: React.FormEvent) {
    e.preventDefault();
    // Guard the submit, not just the button — a disabled button doesn't
    // block Enter-key submission, so without this an empty name or no
    // scopes could still POST.
    if (!canCreate) return;
    setErr(null);
    setBusy(true);
    try {
      const k = await api.createKey({ name: name.trim(), scopes });
      // Carry the submitted scopes onto the reveal — the response omits them.
      setCreated({ ...k, scopes });
      setName("");
      refresh();
    } catch (ex) {
      setErr(String((ex as Error).message));
    } finally {
      setBusy(false);
    }
  }

  function toggleScope(id: string, on: boolean) {
    setScopes((cur) => (on ? [...cur, id] : cur.filter((x) => x !== id)));
  }

  async function revoke(id: string) {
    setConfirming(null);
    try {
      await api.revokeKey(id);
      refresh();
    } catch (ex) {
      setErr(String((ex as Error).message));
    }
  }

  const activeCount = keys?.filter((k) => k.status === "active").length ?? 0;
  const revokedCount = keys ? keys.length - activeCount : 0;
  const visibleKeys = keys
    ? keys.filter((k) => showRevoked || k.status === "active")
    : [];

  return (
    <div className="main">
      <div className="page-header">
        <h1>API keys</h1>
        {activeCount > 0 && (
          <span className="badge">
            {activeCount} active
          </span>
        )}
      </div>
      <p className="muted" style={{ marginTop: 0, maxWidth: "64ch" }}>
        API keys authenticate your own code against the REST API. Call the
        api origin with <code>{REST_AUTH_EXAMPLE}</code>. Keys are
        organisation-owned and scoped; the secret is shown once on creation
        and stored only as a hash. Rotate by creating a new key and revoking
        the old one. To connect an AI agent instead, use{" "}
        <Link to="/connected-apps">Agents</Link> — no key required.
      </p>

      {created && (
        <div className="card key-reveal">
          <h2>Your new API key</h2>
          <div className="callout warn">
            <span aria-hidden>⚠</span>
            <span>
              Copy this now — it’s the only time the full secret is shown.
              Store it in a secret manager, never in source control.
            </span>
          </div>
          <div className="endpoint" style={{ marginTop: "var(--space-4)" }}>
            <code className="key">{created.key}</code>
            <CopyButton
              value={created.key}
              className="btn"
              label="Copy key"
            />
          </div>
          <div className="chip-row">
            {created.scopes.map((s) => (
              <span className="chip" key={s} title={s}>
                {SCOPE_LABEL[s] ?? s}
              </span>
            ))}
          </div>
          <button
            className="btn secondary"
            style={{ marginTop: "var(--space-4)" }}
            onClick={() => setCreated(null)}
          >
            Done — I’ve saved it
          </button>
        </div>
      )}

      <div className="card">
        <h2>Create a key</h2>
        <form onSubmit={create} className="key-form">
          <div>
            <label className="field-label" htmlFor="key-name">
              Name
            </label>
            <input
              id="key-name"
              className="input"
              style={{ width: "min(420px, 100%)" }}
              placeholder="e.g. Production ingest"
              value={name}
              required
              onChange={(e) => setName(e.target.value)}
            />
            <p className="key-hint">
              A label to recognise this key later — where it runs or what
              it’s for.
            </p>
          </div>

          {/* role=group + aria-labelledby restores the checkbox-group
              semantics a <fieldset>/<legend> would give, without the
              default fieldset chrome the card grid doesn't want. */}
          <div role="group" aria-labelledby="perm-label">
            <div className="field-label" id="perm-label">
              Permissions
            </div>
            <div className="scope-cards">
              {SCOPES.map((s) => {
                const on = scopes.includes(s.id);
                return (
                  <label key={s.id} className={`scope-card${on ? " on" : ""}`}>
                    <input
                      type="checkbox"
                      checked={on}
                      onChange={(e) => toggleScope(s.id, e.target.checked)}
                    />
                    <span className="scope-card-body">
                      <span className="scope-card-name">
                        {s.label}
                        <code>{s.id}</code>
                      </span>
                      <span className="scope-card-desc">{s.description}</span>
                    </span>
                  </label>
                );
              })}
            </div>
            {scopes.length === 0 && (
              <p className="key-hint">Select at least one permission.</p>
            )}
          </div>

          <div>
            <button className="btn primary" type="submit" disabled={!canCreate}>
              {busy ? "Creating…" : "Create key"}
            </button>
          </div>
        </form>
      </div>

      {err && <p className="error">{err}</p>}

      <div className="list-head">
        <h2>Your keys</h2>
        {keys && revokedCount > 0 && (
          <label className="revoked-toggle">
            <input
              type="checkbox"
              checked={showRevoked}
              onChange={(e) => setShowRevoked(e.target.checked)}
            />
            Show revoked ({revokedCount})
          </label>
        )}
      </div>
      {keys === null && <p className="muted">Loading…</p>}

      {keys && keys.length === 0 && (
        <div className="agents-empty">
          <span
            className="hero-mark"
            aria-hidden
            dangerouslySetInnerHTML={{ __html: keySvg }}
          />
          <h2>No keys yet</h2>
          <p className="muted">
            Create a key above to call the REST API directly from your own
            code.
          </p>
        </div>
      )}

      {keys && keys.length > 0 && visibleKeys.length === 0 && (
        <p className="muted">
          {revokedCount === 1
            ? "Your only key is revoked — tick “Show revoked” to see it."
            : `All ${revokedCount} keys are revoked — tick “Show revoked” to see them.`}
        </p>
      )}

      {keys && visibleKeys.length > 0 && (
        <div className="entity-list">
          {visibleKeys.map((k) => {
            const initial = k.name.trim().charAt(0).toUpperCase() || "K";
            const revoked = k.status !== "active";
            return (
              <div
                className={`entity-card${revoked ? " is-revoked" : ""}`}
                key={k.id}
              >
                <span className="entity-avatar" aria-hidden>
                  {initial}
                </span>
                <div className="entity-body">
                  <div className="entity-name">{k.name}</div>
                  <div className="entity-meta">
                    {k.status === "active" ? (
                      <span className="pill ok">
                        <span className="pill-dot" aria-hidden />
                        Active
                      </span>
                    ) : (
                      <span className="pill">
                        <span className="pill-dot" aria-hidden />
                        Revoked
                      </span>
                    )}
                    <span className="mono">
                      {k.prefix}…{k.last4}
                    </span>
                    <span title={new Date(k.created_at).toLocaleString()}>
                      Created {relativeTime(k.created_at)}
                    </span>
                    {k.last_used_at ? (
                      <span title={new Date(k.last_used_at).toLocaleString()}>
                        Last used {relativeTime(k.last_used_at)}
                      </span>
                    ) : (
                      <span>Never used</span>
                    )}
                  </div>
                  {k.scopes.length > 0 && (
                    <div className="chip-row">
                      {k.scopes.map((s) => (
                        <span className="chip" key={s} title={s}>
                          {SCOPE_LABEL[s] ?? s}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
                {k.status === "active" && (
                  <div className="entity-actions">
                    {confirming === k.id ? (
                      <>
                        <button
                          className="btn danger"
                          onClick={() => revoke(k.id)}
                        >
                          Confirm revoke
                        </button>
                        <button
                          className="btn secondary"
                          onClick={() => setConfirming(null)}
                        >
                          Cancel
                        </button>
                      </>
                    ) : (
                      <button
                        className="btn secondary"
                        onClick={() => setConfirming(k.id)}
                      >
                        Revoke
                      </button>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
